import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { isProcessDevice } from '@/tauri/audioDeviceUtils';
import {
  SYSTEM_DEFAULT_DEVICE_MARKER,
  SYSTEM_WIDE_DEVICE_MARKER,
} from '@/tauri/constants';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import { type SessionStatus } from '@heidi/schema/ml-scribe';
import type { OutputSourceType } from '@heidi/session/contracts';
import { type AudioDevice } from 'src-tauri/src/bindings/AudioDevice';

/**
 * App-side emit hosts for two of `session_updated`'s five
 * `update_action` arms (`@heidi/session`).
 *
 * ROUTING IS NOT UNIFORM ACROSS THE TWO and was deliberately not normalised:
 * both suppress Amplitude, but they pass
 * `options` AFTER `props` — same object, and the suite pins the wire tuple per
 * event rather than the key order.
 *
 * TWELVE WRAPPERS MINTED A DECLARED NAME AND TWO OF THEM STAY, which is a
 * measurement rather than an omission: `sessionChooseMicInput` and
 * `sessionDateTimeUpdated` each emit a V1 `trackEvent` beside the V2 one and a
 * `track()` host has no V1 leg, so converting either would silently drop a
 * Segment series. `session_name_updated` already emits through `track()`.
 *
 * `session-wrapper-partition.test.ts` sweeps the model and measures what is left:
 * eleven wrappers still mint a session wire name, ten of them V1-legged, and the
 * one that is not mints a name this row does not declare.
 */

export const trackSessionStatusUpdated = (input: {
  sessionId: string;
  sessionStatus: SessionStatus;
}): void => {
  track({
    event_name: TrackingEventName.SessionUpdated,
    props: {
      session_id: input.sessionId,
      update_action: 'session_status_updated',
      value: input.sessionStatus,
    },
    options: { amplitude: false },
  });
};

/**
 * Desktop speaker/output source selection. Pass the selected `AudioDevice` (or
 * the `__SYSTEM_DEFAULT__` marker); the label and kind are derived here so every
 * call site reports one vocabulary. No-ops off a session, hence no `session_id`.
 */
export const trackSessionOutputSourceChanged = (device: AudioDevice): void => {
  const sessionId = getCurrentSessionId();
  if (!sessionId) return;

  const isSystemDefault = device.name === SYSTEM_DEFAULT_DEVICE_MARKER;
  // The "System audio" whole-system entry is a sentinel too — report the
  // stable value, never the raw `__SYSTEM_WIDE__` marker.
  const isSystemWide = device.name === SYSTEM_WIDE_DEVICE_MARKER;
  const outputSourceType: OutputSourceType = isSystemWide
    ? 'system_wide'
    : isSystemDefault
      ? 'system_default'
      : isProcessDevice(device)
        ? 'app'
        : 'device';
  // Process entries are named `__PROCESS__:<pid>` — the PID is meaningless
  // in dashboards, so report the app's display name instead.
  const outputSource = isSystemWide
    ? 'system_wide'
    : isSystemDefault
      ? 'system_default'
      : (device.display_name ?? device.name);

  track({
    event_name: TrackingEventName.SessionUpdated,
    props: {
      session_id: sessionId,
      update_action: 'output_source_changed',
      output_source: outputSource,
      output_source_type: outputSourceType,
    },
    options: { amplitude: false },
  });
};
